import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Droplets, Wind, Eye, RefreshCw, MapPin, AlertTriangle, Navigation } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import { weatherAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MODULES } from '../constants/modules';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function getConditionEmoji(code) {
  if (!code) return '🌤';
  if (code === 1000) return '☀️';
  if (code <= 1003) return '⛅';
  if (code <= 1009) return '☁️';
  if (code <= 1030) return '🌫';
  if (code <= 1087) return '⛈';
  if (code <= 1117) return '❄️';
  if (code <= 1147) return '🌨';
  if (code <= 1201) return '🌧';
  if (code <= 1225) return '❄️';
  if (code <= 1246) return '🌧';
  if (code <= 1282) return '🌨';
  return '🌤';
}

function getRainRisk(chance) {
  if (chance >= 70) return { label: 'High rain risk', color: 'text-red-600', bg: 'bg-red-50 border-red-200', dot: 'bg-red-500' };
  if (chance >= 40) return { label: 'Moderate rain risk', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500' };
  return { label: 'Low rain risk', color: 'text-green-600', bg: 'bg-green-50 border-green-200', dot: 'bg-green-500' };
}

function getRainBadgeStyle(chance) {
  if (chance >= 70) return 'bg-red-100 text-red-700 font-bold';
  if (chance >= 40) return 'bg-amber-100 text-amber-700 font-semibold';
  return 'text-ga-text-muted';
}

const CACHE_KEY_PREFIX = 'weather_cache_';
const CACHE_TTL = 30 * 60 * 1000;

export function clearAllWeatherCaches() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}

function getSessionCache(facilityId) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${facilityId}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${facilityId}`); return null; }
    return data;
  } catch { return null; }
}

function setSessionCache(facilityId, data) {
  try {
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${facilityId}`, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* ignore */ }
}

// ─── shared fetch hook ────────────────────────────────────────────────────────
export function useWeather(facilityOverride) {
  const { activeFacility, activeFacilityId, allowedFacilities } = useFacility();
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState('idle');

  const resolvedFacility = useMemo(() => {
    const hasLocation = (f) =>
      f && (f.latitude != null || f.longitude != null || (f.city && String(f.city).trim()));
    if (hasLocation(facilityOverride)) return facilityOverride;
    if (activeFacility) return activeFacility;
    if (activeFacilityId != null) {
      return allowedFacilities.find(
        (f) => Number(f.facility_id) === Number(activeFacilityId)
      ) || null;
    }
    return null;
  }, [facilityOverride, activeFacility, activeFacilityId, allowedFacilities]);

  const facilityIdForWeather = useMemo(() => {
    if (activeFacilityId != null) return activeFacilityId;
    const f = resolvedFacility;
    if (!f) return null;
    const raw = f.facility_id ?? f.id ?? f.facilityId;
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Number.isNaN(n) ? null : n;
  }, [activeFacilityId, resolvedFacility]);

  const cacheKeyForFacility = useCallback((facility, facilityId) => {
    if (facilityId != null) return facilityId;
    const lat = facility?.latitude;
    const lng = facility?.longitude;
    if (lat != null && lng != null) return `coords_${lat}_${lng}`;
    if (facility?.city) return `city_${facility.city}`;
    return 'default';
  }, []);

  const fetch_ = useCallback(async (facility, facilityId) => {
    if (!facility) {
      setStatus(activeFacilityId != null ? 'no-location' : 'idle');
      return;
    }
    const lat = facility.latitude;
    const lng = facility.longitude;
    const city = facility.city;
    if (!lat && !lng && !city) { setStatus('no-location'); return; }
    const cacheId = cacheKeyForFacility(facility, facilityId);
    const cached = getSessionCache(cacheId);
    if (cached) { setWeather(cached); setStatus('success'); return; }
    setStatus('loading');
    try {
      const res = await weatherAPI.getForFacility(lat && lng ? { lat, lng } : { city });
      if (res?.success && res.data) {
        setWeather(res.data);
        setSessionCache(cacheId, res.data);
        setStatus('success');
      } else { setStatus('error'); }
    } catch { setStatus('error'); }
  }, [cacheKeyForFacility]);

  const refresh = useCallback(() => {
    const cacheId = cacheKeyForFacility(resolvedFacility, facilityIdForWeather);
    sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${cacheId}`);
    if (resolvedFacility) fetch_(resolvedFacility, facilityIdForWeather);
  }, [resolvedFacility, facilityIdForWeather, fetch_, cacheKeyForFacility]);

  useEffect(() => {
    if (resolvedFacility) fetch_(resolvedFacility, facilityIdForWeather);
    else if (activeFacilityId != null || facilityIdForWeather != null) setStatus('no-location');
    else setStatus('idle');
  }, [resolvedFacility, facilityIdForWeather, activeFacilityId, fetch_]);

  return {
    weather,
    status,
    refresh,
    activeFacility: resolvedFacility,
    activeFacilityId: facilityIdForWeather,
  };
}

// ─── WeatherCurrentPanel — fits inside header card right column ───────────────
export const WeatherCurrentPanel = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(MODULES.CONFIGURATION, 'write');
  const { weather, status, refresh, activeFacility } = useWeather();

  if (status === 'no-location') {
    if (!canManage) return null;
    return (
      <div className="flex flex-col gap-2 h-full justify-center">
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-amber-800">Ground location not set</p>
            <p className="text-xs text-amber-700 mt-0.5">Set GPS to show live weather.</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/facility-location')}
          className="flex items-center gap-1.5 text-xs font-medium text-ga-primary hover:underline"
        >
          <Navigation className="h-3 w-3" />
          Set ground location
        </button>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-ga-text-muted h-full">
        <RefreshCw className="h-4 w-4 animate-spin text-ga-primary" />
        <span className="text-sm">Loading weather…</span>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center gap-2 text-ga-text-muted h-full">
        <span className="text-sm">Weather unavailable</span>
        <button onClick={refresh} className="text-xs text-ga-primary hover:underline">Retry</button>
      </div>
    );
  }

  if (status !== 'success' || !weather) return null;

  const { current, forecast, location } = weather;
  const today = forecast?.[0];
  const rainRisk = getRainRisk(today?.chance_of_rain ?? 0);
  const usingPrecise = activeFacility?.latitude != null;

  return (
    <div className="flex flex-col gap-3">
      {/* Temp + condition */}
      <div className="flex items-start gap-3">
        <span className="text-4xl leading-none">{getConditionEmoji(current.condition.code)}</span>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-ga-text-primary">{current.temp_c}°C</span>
            <span className="text-sm text-ga-text-muted">feels {current.feels_like_c}°C</span>
          </div>
          <p className="text-sm text-ga-text-muted">{current.condition.text}</p>
        </div>
      </div>

      {/* Location */}
      <div className="flex items-center gap-1.5">
        <MapPin className="h-3.5 w-3.5 text-ga-primary shrink-0" />
        <span className="text-xs text-ga-text-muted">{location.name}, {location.region}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${usingPrecise ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {usingPrecise ? 'Precise GPS' : 'City-level'}
        </span>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-4 text-xs text-ga-text-muted">
        <span className="flex items-center gap-1">
          <Droplets className="h-3.5 w-3.5 text-blue-500" />
          <span className="font-medium text-ga-text-primary">{current.humidity}%</span> Humidity
        </span>
        <span className="flex items-center gap-1">
          <Wind className="h-3.5 w-3.5 text-teal-500" />
          <span className="font-medium text-ga-text-primary">{current.wind_kph} km/h</span> {current.wind_dir}
        </span>
        <span className="flex items-center gap-1">
          <Eye className="h-3.5 w-3.5 text-gray-400" />
          <span className="font-medium text-ga-text-primary">{current.vis_km} km</span>
        </span>
      </div>

      {/* Rain risk */}
      {today && (
        <div className={`flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs ${rainRisk.bg}`}>
          <span className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${rainRisk.dot}`} />
            <span className={`font-medium ${rainRisk.color}`}>{rainRisk.label} today</span>
          </span>
          <span className={rainRisk.color}>{today.chance_of_rain}% · {today.total_precip_mm}mm</span>
        </div>
      )}

      {/* Admin prompt for imprecise location */}
      {canManage && !usingPrecise && (
        <button
          onClick={() => navigate('/dashboard/facility-location')}
          className="flex items-center gap-1 text-xs text-ga-primary hover:underline self-start"
        >
          <Navigation className="h-3 w-3" />
          Set precise GPS for better accuracy
        </button>
      )}
    </div>
  );
};

// ─── WeatherForecastStrip — 7-day strip below the header card ────────────────
export const WeatherForecastStrip = () => {
  const { weather, status, refresh } = useWeather();

  if (status !== 'success' || !weather?.forecast?.length) return null;

  const { forecast, location } = weather;
  const updatedTime = new Date(location.localtime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="ga-card rounded-xl px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-ga-text-muted uppercase tracking-wide">
          7-Day Forecast
        </span>
        <div className="flex items-center gap-3">
          <span
            className="text-xs text-ga-text-muted cursor-default"
            title={`Weather data as of ${new Date(location.localtime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} · refreshes every 30 min`}
          >
            Updated {updatedTime}
          </span>
          <button
            onClick={refresh}
            className="text-ga-text-muted hover:text-ga-text-primary transition-colors"
            title="Refresh weather"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Day columns — horizontally scrollable on mobile */}
      <div className="grid grid-cols-7 gap-1 overflow-x-auto">
        {forecast.map((day, idx) => {
          const d = new Date(day.date);
          const dayLabel = idx === 0 ? 'Today' : DAY_NAMES[d.getDay()];
          const rain = day.chance_of_rain;
          const rainStyle = getRainBadgeStyle(rain);
          const isHighRisk = rain >= 70;
          const isMidRisk = rain >= 40 && rain < 70;

          return (
            <div
              key={day.date}
              className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg ${
                isHighRisk ? 'border border-red-200 border-l-[3px] border-l-red-500' :
                isMidRisk  ? 'border border-amber-200 border-l-[3px] border-l-amber-500' :
                'border border-transparent'
              }`}
            >
              <span className={`text-xs font-medium ${idx === 0 ? 'text-ga-primary' : 'text-ga-text-muted'}`}>
                {dayLabel}
              </span>
              <span className="text-xl leading-none">{getConditionEmoji(day.condition.code)}</span>
              <span className="text-xs font-semibold text-ga-text-primary whitespace-nowrap">
                {Math.round(day.max_c)}°
                <span className="text-ga-text-muted font-normal">/{Math.round(day.min_c)}°</span>
              </span>
              {rain > 0 ? (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${rainStyle}`}>
                  {rain}%
                </span>
              ) : (
                <span className="text-xs text-ga-text-muted">0%</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Default export: standalone widget (kept for backward compat) ─────────────
const WeatherWidget = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(MODULES.CONFIGURATION, 'write');
  const { weather, status, refresh, activeFacility, activeFacilityId } = useWeather();

  if (status === 'no-location') {
    if (!canManage) return null;
    return (
      <div className="ga-card rounded-xl p-4 flex items-start gap-3 border border-amber-200 bg-amber-50">
        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800">Ground location not set</p>
          <p className="text-xs text-amber-700 mt-0.5">Set GPS coordinates to see live weather for this ground.</p>
        </div>
        <button onClick={() => navigate('/dashboard/facility-location')} className="shrink-0 text-xs font-medium text-amber-700 underline hover:text-amber-900">
          Set location
        </button>
      </div>
    );
  }
  if (status === 'loading') return (
    <div className="ga-card rounded-xl p-4 flex items-center gap-3">
      <RefreshCw className="h-5 w-5 text-ga-primary animate-spin" />
      <span className="text-sm text-ga-text-muted">Loading weather…</span>
    </div>
  );
  if (status === 'error') return (
    <div className="ga-card rounded-xl p-4 flex items-center justify-between gap-3">
      <span className="text-sm text-ga-text-muted">Weather unavailable</span>
      <button onClick={refresh} className="text-xs text-ga-primary underline">Retry</button>
    </div>
  );
  if (status !== 'success' || !weather) return null;

  return (
    <div className="space-y-4">
      <WeatherForecastStrip />
    </div>
  );
};

export default WeatherWidget;
